import { createFileRoute, Link } from '@tanstack/react-router';
import { useAgentsList, useStatusCounts, type AgentListParams, type StatusCounts } from '@/hooks/use-agents';
import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '@/hooks/use-auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Edit, Trash2, Plus, ChevronLeft, ChevronRight, ArrowUpDown, ArrowDown } from 'lucide-react';

type StatusFilter = 'all' | 'pending' | 'draft' | 'published' | 'unpublished' | 'rejected';

const STATUS_OPTIONS: Array<{ key: StatusFilter; label: string; countKey: keyof StatusCounts }> = [
  { key: 'all', label: 'All', countKey: 'all' },
  { key: 'pending', label: 'Pending', countKey: 'pending' },
  { key: 'draft', label: 'Draft', countKey: 'draft' },
  { key: 'published', label: 'Published', countKey: 'published' },
  { key: 'unpublished', label: 'Unpublished', countKey: 'unpublished' },
  { key: 'rejected', label: 'Rejected', countKey: 'rejected' },
];

const STATUS_BADGE_CLASS: Record<
  StatusFilter,
  {
    label: string;
    className: string;
  }
> = {
  all: { label: 'All', className: 'bg-gray-100 text-gray-600' },
  pending: { label: 'Pending', className: 'bg-amber-100 text-amber-700' },
  draft: { label: 'Draft', className: 'bg-sky-100 text-sky-700' },
  published: { label: 'Published', className: 'bg-green-100 text-green-800' },
  unpublished: { label: 'Unpublished', className: 'bg-red-100 text-red-800' },
  rejected: { label: 'Rejected', className: 'bg-rose-100 text-rose-700' },
};

function cn(...classes: (string | false | null | undefined)[]) {
  return classes.filter(Boolean).join(' ');
}

export const Route = createFileRoute('/agents/')({
  component: AgentsListPage,
});

function AgentsListPage() {
  const { user, loading: authLoading } = useAuth();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<'createdAt' | 'updatedAt'>('updatedAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const pageSize = 10;

  const params: AgentListParams = {
    page: currentPage,
    pageSize,
    query: debouncedQuery || undefined,
    statusFilter: statusFilter !== 'all' ? statusFilter : undefined,
    sortBy,
    sortDir,
  };

  const { data: agentsData, isLoading, error } = useAgentsList(params);
  const { data: statusCounts } = useStatusCounts();

  const counts = statusCounts ?? {
    all: 0,
    pending: 0,
    draft: 0,
    published: 0,
    unpublished: 0,
    rejected: 0,
  };

  const handleSearch = useCallback(() => {
    setDebouncedQuery(query.trim());
    setCurrentPage(1);
  }, [query]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSearch();
      }
    },
    [handleSearch]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedQuery, statusFilter]);

  const agents = agentsData?.agents ?? [];
  const totalPages = agentsData?.totalPages ?? 1;
  const totalItems = agentsData?.total ?? 0;
  const startIndex = (currentPage - 1) * pageSize;

  // Format time ago - matches HTML format
  const formatTimeAgo = (dateString: string | undefined) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
    if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
  };

  return (
    <div className="space-y-6">

      {/* Filters Section - Horizontal layout like HTML */}
      <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
        <div className="flex items-center space-x-6">
          {STATUS_OPTIONS.map((option, index) => {
            const count = counts[option.countKey] ?? 0;
            const isActive = statusFilter === option.key;
            return (
              <div key={option.key} className="flex items-center space-x-6">
                {index > 0 && <div className="w-px h-4 bg-gray-200"></div>}
                <button
                  onClick={() => setStatusFilter(option.key)}
                  className={cn(
                    'flex items-center space-x-2 transition-colors',
                    isActive ? 'text-brand-yellow font-medium' : 'text-gray-600 hover:text-gray-900'
                  )}
                >
                  <span className="text-sm">{option.label}</span>
                  <span className={cn('text-xs px-2 py-1 rounded-full', STATUS_BADGE_CLASS[option.key].className)}>
                    {count}
                  </span>
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Search Bar - Simple layout like HTML */}
      <div className="flex items-center space-x-3">
        <div className="flex-1 relative">
          <Input
            type="text"
            placeholder="Search by name, slug, or provider..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-full pl-4 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-yellow-200 focus:border-brand-yellow outline-none text-sm"
          />
        </div>
        <button
          onClick={handleSearch}
          className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <Search className="h-4 w-4 text-gray-500" />
        </button>
      </div>

      {/* Agents Table - Clean minimalist design like HTML */}
      {isLoading ? (
        <div className="flex min-h-[320px] flex-col items-center justify-center gap-3 rounded-lg border border-gray-200 bg-white py-16">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-brand-yellow border-t-transparent"></div>
          <p className="text-sm text-gray-500">Đang tải danh sách agents...</p>
        </div>
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm font-medium text-red-600">Không thể tải dữ liệu: {error.message}</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center space-x-1">
                    <span>Agent</span>
                    <button
                      type="button"
                      onClick={() => {
                        setSortBy('updatedAt');
                        setSortDir((prev) => (prev === 'desc' ? 'asc' : 'desc'));
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <ArrowUpDown className="h-3 w-3" />
                    </button>
                  </div>
                </th>
                <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Provider
                </th>
                <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center space-x-1">
                    <span>Last Updated</span>
                    <button
                      type="button"
                      onClick={() => {
                        setSortBy('updatedAt');
                        setSortDir((prev) => (prev === 'desc' ? 'asc' : 'desc'));
                      }}
                      className="text-brand-yellow"
                    >
                      <ArrowDown className={cn('h-3 w-3', sortDir === 'desc' ? '' : 'rotate-180')} />
                    </button>
                  </div>
                </th>
                <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {agents.map((agent) => {
                const moderationKey = (agent.moderationStatus ?? 'pending') as StatusFilter;
                const statusInfo = STATUS_BADGE_CLASS[moderationKey] ?? STATUS_BADGE_CLASS.pending;
                const descriptionText = agent.description
                  ? agent.description.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()
                  : "";
                const shortDescription =
                  descriptionText.length > 60 ? `${descriptionText.slice(0, 57)}...` : descriptionText;
                const firstTag = agent.tags && agent.tags.length > 0 ? agent.tags[0] : null;
                return (
                  <tr key={agent.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <Link
                          to="/agents/$id"
                          params={{ id: agent.id }}
                          className="text-sm font-medium text-gray-900 hover:text-brand-yellow transition-colors"
                        >
                          {agent.name}
                        </Link>
                        {shortDescription && (
                          <div className="text-sm text-gray-500 mt-1">{shortDescription}</div>
                        )}
                        {firstTag && (
                          <div className="mt-1">
                            <span
                              className={cn(
                                'inline-block text-xs px-2 py-1 rounded-full',
                                firstTag === 'AI'
                                  ? 'bg-blue-100 text-blue-800'
                                  : firstTag === 'Conversational'
                                  ? 'bg-purple-100 text-purple-800'
                                  : 'bg-orange-100 text-orange-800'
                              )}
                            >
                              {firstTag}
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{agent.provider || '—'}</td>
                    <td className="px-6 py-4">
                      <span className={cn('inline-block text-xs px-2 py-1 rounded-full', statusInfo.className)}>
                        {statusInfo.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatTimeAgo(agent.updatedAt)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <Link to="/agents/$id" params={{ id: agent.id }}>
                          <button className="text-gray-400 hover:text-gray-600 transition-colors">
                            <Edit className="h-4 w-4" />
                          </button>
                        </Link>
                        <button className="text-gray-400 hover:text-red-600 transition-colors">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {agents.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-sm text-gray-500">
                    Không tìm thấy agents phù hợp với bộ lọc hiện tại.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>

          {/* Pagination Footer */}
          {agents.length > 0 && (
            <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-6 py-4">
              <div className="text-sm text-gray-500">
                Hiển thị{' '}
                <span className="font-medium text-gray-700">
                  {totalItems === 0 ? 0 : Math.min(startIndex + 1, totalItems)}–{Math.min(currentPage * pageSize, totalItems)}
                </span>{' '}
                trong tổng số{' '}
                <span className="font-medium text-gray-700">{totalItems}</span> agents
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="border-gray-200 text-gray-600 hover:bg-gray-100"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-xs font-medium text-gray-500 px-2">
                  Trang {currentPage} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages || totalPages === 0}
                  className="border-gray-200 text-gray-600 hover:bg-gray-100"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
